<template>
  <section class="card">
    <h2>活动管理</h2>

    <p class="tip">
      此处维护小程序「活动」Tab 列表与<strong>活动详情页</strong>字段。支持在列表中直接“推到首页热门位”：
      点击“设为首页热门”后，会写入活动表内推荐标记（单表方案，不再依赖独立热门活动表）。
      活动报名固定使用内置表 <code>activity_signup</code>；「活动信息收集」请在<strong>编辑活动</strong>页配置 JSON，不再使用本页的「表单管理」入口。
    </p>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>
    <p v-if="okMsg" class="ok">{{ okMsg }}</p>

    <div class="toolbar-row">
      <button type="button" class="btn btn-primary" @click="goNew">新建活动</button>
    </div>

    <div class="filters filters-panel">
      <div class="flt">
        <input
          v-model.trim="filters.title"
          class="flt-input flt-input--wide"
          placeholder="请输入活动名称"
          aria-label="活动名称"
          @keyup.enter="search"
        />
      </div>
      <div class="flt">
        <FancySelect
          v-model="filters.displayStatus"
          class="flt-select"
          :options="displayStatusOptions"
          placeholder="首页推荐"
          @change="search"
        />
      </div>
      <div class="flt">
        <FancySelect
          v-model="filters.publishStatus"
          class="flt-select"
          :options="publishStatusOptions"
          placeholder="启用状态"
          @change="search"
        />
      </div>
      <div class="flt">
        <FancySelect
          v-model="filters.activityStatus"
          class="flt-select"
          :options="activityStatusOptions"
          placeholder="列表状态"
          @change="search"
        />
      </div>
      <div class="flt flt--range">
        <span class="flt-range">
          <input
            v-model="filters.activityTimeStart"
            type="date"
            class="flt-input flt-input--date"
            aria-label="活动开始时间"
            @change="search"
          />
          <span class="flt-sep">至</span>
          <input
            v-model="filters.activityTimeEnd"
            type="date"
            class="flt-input flt-input--date"
            aria-label="活动结束时间"
            @change="search"
          />
        </span>
      </div>
      <div class="filter-actions">
        <button type="button" class="btn btn-primary filter-action-btn" @click="search">查询</button>
        <button type="button" class="btn btn-cancel filter-action-btn" @click="resetFilters">重置</button>
      </div>
    </div>

    <div class="table-scroll">
      <table class="activity-table">
        <thead>
          <tr>
            <th class="col-seq">序号</th>
            <th class="col-center">标题</th>
            <th class="col-center col-time">活动时间</th>
            <th class="col-num">列表状态</th>
            <th class="col-num">名额</th>
            <th class="col-num">已报</th>
            <th class="col-num">报名费用</th>
            <th class="col-num">待审核</th>
            <th class="col-num">浏览次数</th>
            <th class="col-num">首页推荐</th>
            <th class="col-num">状态</th>
            <th class="col-sticky-right col-ops">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in pagedRows" :key="row.id">
            <td class="col-seq">{{ rowOffset + idx + 1 }}</td>
            <td class="col-center col-title">{{ row.title }}</td>
            <td class="col-center col-time">{{ row.activityTimeLabel }}</td>
            <td class="col-num">{{ row.listStateLabel }}</td>
            <td class="col-num">{{ row.signupLimitLabel }}</td>
            <td class="col-num">{{ row.signupCount }}</td>
            <td class="col-num">{{ row.signupFeeLabel }}</td>
            <td class="col-num">
              <span class="pill" :class="row.pendingReviewCount > 0 ? 'pill-warn' : 'pill-gray'">
                {{ row.pendingReviewCount }}
              </span>
            </td>
            <td class="col-num">{{ row.viewCount }}</td>
            <td class="col-num">
              <span class="pill" :class="row.homeFeatured ? 'pill-orange' : 'pill-gray'">
                {{ row.homeFeatured ? '已推荐' : '未推荐' }}
              </span>
            </td>
            <td class="col-num">
              <StatusSwitch
                :model-value="row.statusCode === 'ENABLED'"
                @change="(v) => setActivityStatus(row, v)"
              />
            </td>
            <td class="col-sticky-right col-ops">
              <button type="button" class="btn btn-edit" @click="markFeatured(row)">
                {{ row.homeFeatured ? '取消首页推荐' : '设为首页热门' }}
              </button>
              <button type="button" class="btn btn-edit" @click="openRegs(row)">报名记录</button>
              <button type="button" class="btn btn-edit" @click="openViewLogs(row)">浏览记录</button>
              <button type="button" class="btn btn-edit" @click="goEdit(row.id)">编辑</button>
              <button type="button" class="btn btn-delete" @click="onDelete(row)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <ListPager
      v-model:page="page"
      v-model:page-size="pageSize"
      :total="sortedRows.length"
    />
    <p v-if="!sortedRows.length && !errorMsg" class="muted">暂无活动，请先执行 SQL 建表或点击「新建活动」。</p>
  </section>
</template>

<script>
import { adminConfirm } from '@/utils/adminDialog'
import {
  deleteAdminCmsActivity,
  getAdminCmsActivities,
  getAdminCmsActivity,
  updateAdminCmsActivity,
} from '@/api'
import FancySelect from '@/components/FancySelect.vue'
import ListPager from '@/components/ListPager.vue'
import StatusSwitch from '@/components/StatusSwitch.vue'
import { formatActivityTimeRange } from '@/utils/formatDateTime'
import { clampPage, pageRowOffset, slicePageRows } from '@/utils/listPager'

const FILTER_ALL = '__ALL__'

const EMPTY_FILTERS = () => ({
  title: '',
  displayStatus: '',
  publishStatus: '',
  activityStatus: '',
  activityTimeStart: '',
  activityTimeEnd: '',
})

function normalizeFilterSelect(raw) {
  const s = String(raw || '').trim()
  if (!s || s === FILTER_ALL) return ''
  return s
}

export default {
  name: 'ActivityListManageView',
  components: { FancySelect, ListPager, StatusSwitch },
  data() {
    return {
      rows: [],
      page: 1,
      pageSize: 10,
      filters: EMPTY_FILTERS(),
      errorMsg: '',
      okMsg: '',
      displayStatusOptions: [
        { value: FILTER_ALL, label: '全部' },
        { value: 'featured', label: '已推荐' },
        { value: 'not_featured', label: '未推荐' },
      ],
      publishStatusOptions: [
        { value: FILTER_ALL, label: '全部' },
        { value: 'ENABLED', label: '启用' },
        { value: 'DISABLED', label: '停用' },
      ],
      activityStatusOptions: [
        { value: FILTER_ALL, label: '全部' },
        { value: 'ongoing', label: '进行中' },
        { value: 'ended', label: '已结束' },
      ],
    }
  },
  computed: {
    /** 与后端列表一致：按 sortOrder 升序，再按 id 升序；序号列据此从 1 递增 */
    sortedRows() {
      return (this.rows || [])
        .slice()
        .sort((a, b) => {
          const sa = Number(a.sortOrder)
          const sb = Number(b.sortOrder)
          const oa = Number.isFinite(sa) ? sa : 0
          const ob = Number.isFinite(sb) ? sb : 0
          if (oa !== ob) return oa - ob
          return Number(a.id || 0) - Number(b.id || 0)
        })
    },
    rowOffset() {
      return pageRowOffset(this.page, this.pageSize)
    },
    pagedRows() {
      return slicePageRows(this.sortedRows, this.page, this.pageSize)
    },
  },
  watch: {
    'sortedRows.length'(n) {
      this.page = clampPage(this.page, n, this.pageSize)
    },
  },
  mounted() {
    const tab = String(this.$route?.query?.tab || '').trim().toLowerCase()
    if (tab === 'form') {
      this.$router.replace({ path: '/content/activities', query: {} })
    }
    this.load()
  },
  methods: {
    norm(raw) {
      const signupLimit = raw.signupLimit != null ? Number(raw.signupLimit) : (raw.signup_limit != null ? Number(raw.signup_limit) : null)
      const isRecommended = raw.isRecommended != null
        ? Number(raw.isRecommended)
        : (raw.is_recommended != null ? Number(raw.is_recommended) : 0)
      const listStateLabel = raw.listStateLabel || raw.list_state_label || '—'
      return {
        id: raw.id,
        title: raw.title || '',
        activityTimeLabel: formatActivityTimeRange(raw),
        dateRange: raw.dateRange || raw.date_range || '',
        listStateLabel,
        sortOrder: raw.sortOrder != null ? raw.sortOrder : raw.sort_order != null ? raw.sort_order : 0,
        statusCode: raw.statusCode || raw.status_code || 'ENABLED',
        signupLimit,
        signupLimitLabel: signupLimit == null || Number.isNaN(signupLimit) || signupLimit <= 0 ? '不限' : String(signupLimit),
        signupCount:
          raw.signupCountDisplay != null
            ? Number(raw.signupCountDisplay)
            : raw.signup_count_display != null
              ? Number(raw.signup_count_display)
              : raw.signupCount != null
                ? Number(raw.signupCount)
                : raw.signup_count != null
                  ? Number(raw.signup_count)
                  : 0,
        signupFeeCent:
          raw.signupFeeCent != null
            ? Number(raw.signupFeeCent)
            : raw.signup_fee_cent != null
              ? Number(raw.signup_fee_cent)
              : 0,
        signupFeeLabel: this.formatSignupFeeLabel(raw),
        pendingReviewCount:
          raw.pendingReviewCount != null
            ? Number(raw.pendingReviewCount)
            : raw.pending_review_count != null
              ? Number(raw.pending_review_count)
              : 0,
        viewCount:
          raw.viewCount != null ? Number(raw.viewCount) : raw.view_count != null ? Number(raw.view_count) : 0,
        isRecommended: isRecommended === 0 ? 0 : 1,
        homeFeatured: isRecommended !== 0,
      }
    },
    formatSignupFeeLabel(raw) {
      const cent =
        raw && raw.signupFeeCent != null
          ? Number(raw.signupFeeCent)
          : raw && raw.signup_fee_cent != null
            ? Number(raw.signup_fee_cent)
            : 0
      if (!Number.isFinite(cent) || cent <= 0) return '免费'
      if (cent % 100 === 0) return `¥${cent / 100}`
      return `¥${(cent / 100).toFixed(2)}`
    },
    buildListQuery() {
      const f = this.filters || {}
      const q = {}
      if (String(f.title || '').trim()) q.title = String(f.title).trim()
      const displayStatus = normalizeFilterSelect(f.displayStatus)
      if (displayStatus) q.displayStatus = displayStatus
      const publishStatus = normalizeFilterSelect(f.publishStatus)
      if (publishStatus) q.publishStatus = publishStatus
      const activityStatus = normalizeFilterSelect(f.activityStatus)
      if (activityStatus) q.activityStatus = activityStatus
      if (String(f.activityTimeStart || '').trim()) q.activityTimeStart = String(f.activityTimeStart).trim()
      if (String(f.activityTimeEnd || '').trim()) q.activityTimeEnd = String(f.activityTimeEnd).trim()
      return q
    },
    search() {
      this.page = 1
      this.load()
    },
    resetFilters() {
      this.filters = EMPTY_FILTERS()
      this.page = 1
      this.load()
    },
    async load() {
      this.errorMsg = ''
      this.okMsg = ''
      try {
        const list = await getAdminCmsActivities(this.buildListQuery())
        this.rows = Array.isArray(list) ? list.map((x) => this.norm(x)) : []
        this.page = clampPage(this.page, this.sortedRows.length, this.pageSize)
      } catch (e) {
        this.errorMsg = e.message || '加载失败'
      }
    },
    goNew() {
      this.$router.push('/content/activities/edit/new')
    },
    goEdit(id) {
      this.$router.push(`/content/activities/edit/${id}`)
    },
    buildActivityUpdatePayload(detail, overrides = {}) {
      return {
        title: String(detail?.title || '').trim(),
        coverUrl: String(detail?.coverUrl || '').trim(),
        subTitle: detail?.subTitle || null,
        dateRange: detail?.dateRange || null,
        activityStartAt: detail?.activityStartAt || null,
        activityEndAt: detail?.activityEndAt || null,
        city: detail?.city || null,
        venue: detail?.venue || null,
        address: detail?.address || null,
        navLat: detail?.navLat != null ? Number(detail.navLat) : null,
        navLng: detail?.navLng != null ? Number(detail.navLng) : null,
        introHtml: detail?.introHtml || null,
        isRecommended: detail?.isRecommended != null ? Number(detail.isRecommended) : 0,
        recommendWeight: detail?.recommendWeight != null ? Number(detail.recommendWeight) : 0,
        recommendStartAt: detail?.recommendStartAt || null,
        recommendEndAt: detail?.recommendEndAt || null,
        signupLimit: detail?.signupLimit != null ? Number(detail.signupLimit) : null,
        signupCount: detail?.signupCount != null ? Number(detail.signupCount) : 0,
        sortOrder: detail?.sortOrder != null ? Number(detail.sortOrder) : 0,
        statusCode: String(detail?.statusCode || 'ENABLED'),
        remark: detail?.remark || null,
        signupFormCode: detail?.signupFormCode || null,
        signupRosterVisibility:
          detail?.signupRosterVisibility || detail?.signup_roster_visibility || 'NONE',
        signupFeeCent:
          detail?.signupFeeCent != null
            ? Number(detail.signupFeeCent)
            : detail?.signup_fee_cent != null
              ? Number(detail.signup_fee_cent)
              : 0,
        consultHtml: detail?.consultHtml != null ? detail.consultHtml : detail?.consult_html != null ? detail.consult_html : null,
        checkinStaffPin:
          detail?.checkinStaffPin != null
            ? String(detail.checkinStaffPin)
            : detail?.checkin_staff_pin != null
              ? String(detail.checkin_staff_pin)
              : '',
        ...overrides,
      }
    },
    async setRecommendedById(id, on) {
      const detail = await getAdminCmsActivity(id)
      const payload = this.buildActivityUpdatePayload(detail, {
        isRecommended: on ? 1 : 0,
      })
      await updateAdminCmsActivity(id, payload)
    },
    async markFeatured(row) {
      if (!row || row.id == null) return
      try {
        const nextRecommended = row.homeFeatured ? 0 : 1
        // 交互：单推荐，替换时提示是否取消当前已推荐活动
        if (nextRecommended === 1) {
          const currentFeatured = (this.rows || []).find((x) => x && x.homeFeatured && Number(x.id) !== Number(row.id))
          if (currentFeatured) {
            const ok = await adminConfirm(`当前已推荐：${currentFeatured.title || currentFeatured.id}。\n是否取消当前推荐并推荐「${row.title || row.id}」？`)
            if (!ok) return
            await this.setRecommendedById(currentFeatured.id, false)
          }
        }
        await this.setRecommendedById(row.id, nextRecommended === 1)
        this.okMsg = nextRecommended ? '已设为首页热门' : '已取消首页热门'
        await this.load()
      } catch (e) {
        this.errorMsg = e.message || '更新首页推荐失败'
      }
    },
    async setActivityStatus(row, enabled) {
      if (!row || row.id == null) return
      try {
        const detail = await getAdminCmsActivity(row.id)
        const nextStatus = enabled ? 'ENABLED' : 'DISABLED'
        const payload = this.buildActivityUpdatePayload(detail, {
          statusCode: nextStatus,
        })
        await updateAdminCmsActivity(row.id, payload)
        this.okMsg = nextStatus === 'ENABLED' ? '已启用' : '已禁用'
        await this.load()
      } catch (e) {
        this.errorMsg = e.message || '切换状态失败'
      }
    },
    openRegs(row) {
      if (!row || row.id == null) return
      this.$router.push(`/content/activity-registrations/${row.id}`)
    },
    openViewLogs(row) {
      if (!row || row.id == null) return
      this.$router.push(`/content/activity-view-logs/${row.id}`)
    },
    async onDelete(row) {
      if (!row || row.id == null) return
      if (!await adminConfirm(`确定删除活动「${row.title}」？`)) return
      try {
        await deleteAdminCmsActivity(row.id)
        this.okMsg = '已删除'
        await this.load()
      } catch (e) {
        this.errorMsg = e.message || '删除失败'
      }
    },
  },
}
</script>

<style scoped>
.card {
  background: #fff;
  border-radius: 12px;
  padding: 18px;
  max-width: 100%;
  box-sizing: border-box;
}
h2 {
  margin: 0 0 10px;
  font-size: 18px;
}
.tip {
  background: #f5f8ff;
  border: 1px solid #dae5ff;
  color: #2a4b8d;
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 10px;
  line-height: 1.55;
  font-size: 13px;
}
.tip a {
  color: #2f6af6;
  font-weight: 600;
}
.toolbar-row {
  margin-bottom: 10px;
}
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  margin-bottom: 12px;
  padding: 14px 16px;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 10px;
}
.filter-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.filter-action-btn {
  min-width: 108px;
  height: 40px;
  padding: 0 28px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  box-sizing: border-box;
}
.filter-actions .btn + .btn {
  margin-left: 0;
}
.flt--range {
  flex-shrink: 0;
}
.flt {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.flt-input {
  width: 160px;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 10px;
  background: #fff;
  font-size: 13px;
}
.flt-input--wide {
  width: 200px;
}
.flt-input--date {
  width: 150px;
}
.flt-select {
  width: 170px;
}
.flt-range {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
}
.flt-sep {
  color: #98a2b3;
  font-size: 14px;
}
:deep(.filters .fs-btn) {
  height: 40px;
  min-width: 170px;
  border-radius: 8px;
}
.table-scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  margin-top: 8px;
  border: 1px solid #edf0f5;
  border-radius: 10px;
  background: #fff;
}
.activity-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
.activity-table th,
.activity-table td {
  border-bottom: 1px solid #edf0f5;
  padding: 10px 12px;
  font-size: 13px;
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
  background: #fff;
}
.activity-table thead th {
  background: #f8fafc;
  color: #344054;
  font-weight: 600;
}
.col-seq {
  width: 52px;
  color: #667085;
}
.col-title {
  min-width: 160px;
}
.col-time {
  min-width: 168px;
}
.col-sticky-right {
  position: sticky;
  right: 0;
  z-index: 2;
  box-shadow: -6px 0 10px rgba(15, 23, 42, 0.06);
}
.activity-table thead .col-sticky-right {
  z-index: 3;
  background: #f8fafc;
}
.col-ops {
  min-width: 440px;
  white-space: normal;
}
.btn {
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  border: 1px solid transparent;
  font-size: 12px;
}
.btn + .btn {
  margin-left: 8px;
}
.btn-primary {
  background: #2f6af6;
  color: #fff;
}
.btn-edit {
  border-color: #bfc9db;
  background: #fff;
  color: #26457d;
}
.btn-delete {
  border-color: #ffc8c2;
  background: #fff1f0;
  color: #d92d20;
}
.pill {
  display: inline-block;
  min-width: 58px;
  text-align: center;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}
.pill-green {
  color: #067647;
  background: #ecfdf3;
  border: 1px solid #abefc6;
}
.pill-red {
  color: #b42318;
  background: #fef3f2;
  border: 1px solid #fecdca;
}
.pill-orange {
  color: #b54708;
  background: #fff6ed;
  border: 1px solid #fedf89;
}
.pill-gray {
  color: #475467;
  background: #f2f4f7;
  border: 1px solid #eaecf0;
}
.pill-warn {
  color: #b54708;
  background: #fff6ed;
  border: 1px solid #fedf89;
}
.col-num {
  text-align: center;
  white-space: nowrap;
}
.pill-blue {
  color: #075985;
  background: #e0f2fe;
  border: 1px solid #bae6fd;
}
.error {
  color: #d92d20;
}
.ok {
  color: #067647;
}
.muted {
  color: #98a2b3;
  font-size: 13px;
}
</style>
